import {
  Box,
  HStack,
  IconButton,
  Image,
  Link,
  Tooltip,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useDisclosure,
  SimpleGrid,
} from '@chakra-ui/react';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import tutorialThumbnail from '../../../assets/img/tutorials/tutorial-thumbnail.png';
import fundamentalsThumbnail from '../../../assets/img/tutorials/fundamentals-thumbnail.png';
import linkingThumbnail from '../../../assets/img/tutorials/linking-thumbnail.png';
import quantitiesThumbnail from '../../../assets/img/tutorials/quantities-thumbnail.png';
import shapesThumbnail from '../../../assets/img/tutorials/shapes-thumbnail.png';
import {
  Panel,
  PanelActionButtons,
  PanelContent,
  PanelHeader,
  PanelTitle,
} from '../../common/Panel';
import SparkelIcon from '../../common/icon/SparkelIcon';
import { useUserTenant } from '../../../services/auth-info';

const TutorialItem = (props: {
  thumbnail: string;
  url?: string;
  onClick?: () => void;
}) => {
  return (
    <Box width={200} height={115} _hover={{ opacity: 0.5 }}>
      {props.url ? (
        <Link href={props.url} isExternal>
          <Image src={props.thumbnail} borderRadius={'lg'} />
        </Link>
      ) : (
        <Box onClick={props.onClick} cursor="pointer">
          <Image src={props.thumbnail} borderRadius={'lg'} />
        </Box>
      )}
    </Box>
  );
};

export const TutorialsBanner = ({
  setShouldShowTutorials,
}: {
  setShouldShowTutorials: (value: string[]) => void;
}): React.ReactElement => {
  const { t } = useTranslation('company');
  const { isProtan } = useUserTenant();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null);
  const [thumbnailsByUrl, setThumbnailsByUrl] = useState<
    Record<string, string>
  >({});
  const [protanVideos, setProtanVideos] = useState<string[]>([]);

  // Lazy-load protan tutorial videos only when isProtan is true
  useEffect(() => {
    if (!isProtan) return;

    const loadVideos = async () => {
      const modules = import.meta.glob(
        '../../../assets/videos/protan-tutorials/*.mp4'
      ) as Record<string, () => Promise<{ default: string }>>;

      const sortedEntries = Object.entries(modules).sort(([a], [b]) =>
        a.localeCompare(b)
      );

      const urls = await Promise.all(
        sortedEntries.map(async ([, loader]) => {
          const mod = await loader();
          return mod.default;
        })
      );

      setProtanVideos(urls);
    };

    loadVideos();
  }, [isProtan]);

  const openVideo = (url: string) => {
    setCurrentVideoUrl(url);
    onOpen();
  };

  const handleClose = () => {
    onClose();
    setCurrentVideoUrl(null);
  };

  useEffect(() => {
    let isCancelled = false;

    const generateThumbnail = async (url: string) => {
      try {
        const dataUrl = await extractVideoFrame(url, 2);
        if (!isCancelled) {
          setThumbnailsByUrl((prev) => ({ ...prev, [url]: dataUrl }));
        }
      } catch {
        // ignore; will fall back to static thumbnail
      }
    };

    protanVideos.forEach((url) => {
      if (!thumbnailsByUrl[url]) {
        generateThumbnail(url);
      }
    });

    return () => {
      isCancelled = true;
    };
    // We intentionally exclude thumbnailsByUrl from deps to avoid re-running for each addition
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protanVideos]);

  async function extractVideoFrame(
    url: string,
    seconds: number
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.src = url;

      const cleanup = () => {
        video.removeAttribute('src');
        video.load();
      };

      const onLoaded = () => {
        const target = Math.min(
          seconds,
          isFinite(video.duration) && video.duration > 0
            ? Math.max(0, video.duration - 1)
            : seconds
        );
        const seekTo = isFinite(target) && target > 0 ? target : 0;
        const onSeeked = () => {
          try {
            const width = video.videoWidth;
            const height = video.videoHeight;
            if (width === 0 || height === 0) {
              cleanup();
              reject(new Error('No video dimensions'));
              return;
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              cleanup();
              reject(new Error('No canvas context'));
              return;
            }
            ctx.drawImage(video, 0, 0, width, height);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
            cleanup();
            resolve(dataUrl);
          } catch (e) {
            cleanup();
            reject(e as Error);
          }
        };
        video.removeEventListener('seeked', onSeeked);
        video.addEventListener('seeked', onSeeked);
        try {
          video.currentTime = seekTo;
        } catch (e) {
          // Some browsers require play() before seek; try a quick muted play-pause
          video
            .play()
            .then(() => {
              video.pause();
              video.currentTime = seekTo;
            })
            .catch(() => reject(e as Error));
        }
      };

      const onError = () => {
        cleanup();
        reject(new Error('Failed to load video'));
      };

      video.addEventListener('loadedmetadata', onLoaded, { once: true });
      video.addEventListener('error', onError, { once: true });
    });
  }
  return (
    <Panel width={isProtan ? undefined : 'fit-content'}>
      <PanelHeader>
        <PanelTitle>{t('getting-started-header')}</PanelTitle>
        <PanelActionButtons>
          <Tooltip label="Close">
            <IconButton
              isRound
              aria-label="select grouping label"
              colorScheme="gray"
              size="xs"
              icon={<SparkelIcon icon={faTimes} />}
              onClick={() => setShouldShowTutorials(['false'])}
            />
          </Tooltip>
        </PanelActionButtons>
      </PanelHeader>
      <PanelContent>
        {isProtan && protanVideos.length > 0 ? (
          <>
            <SimpleGrid columns={3} spacing={2} minChildWidth="200px">
              {protanVideos.map((url) => (
                <TutorialItem
                  key={url}
                  thumbnail={thumbnailsByUrl[url] ?? ''}
                  onClick={() => openVideo(url)}
                />
              ))}
            </SimpleGrid>

            <Modal
              isOpen={isOpen}
              onClose={handleClose}
              isCentered
              size={'6xl'}
            >
              <ModalOverlay />
              <ModalContent>
                <ModalCloseButton />
                <ModalBody padding={0}>
                  {currentVideoUrl ? (
                    <video
                      src={currentVideoUrl}
                      autoPlay
                      controls
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                      }}
                    />
                  ) : undefined}
                </ModalBody>
              </ModalContent>
            </Modal>
          </>
        ) : (
          <HStack gap={2}>
            <TutorialItem
              thumbnail={tutorialThumbnail}
              url="https://www.youtube.com/watch?v=xx8vTKIozSA&list=PLD92677OPLExkYYXecx1r4Npa-jDnBt0T&index=1"
            />
            <TutorialItem
              thumbnail={fundamentalsThumbnail}
              url="https://www.youtube.com/watch?v=BPheyBj-WVI&list=PLD92677OPLExkYYXecx1r4Npa-jDnBt0T&index=2"
            />
            <TutorialItem
              thumbnail={linkingThumbnail}
              url="https://www.youtube.com/watch?v=DGzoZo8Smzo&list=PLD92677OPLExkYYXecx1r4Npa-jDnBt0T&index=3"
            />
            <TutorialItem
              thumbnail={quantitiesThumbnail}
              url="https://www.youtube.com/watch?v=x7Ei_p-BWWQ&list=PLD92677OPLExkYYXecx1r4Npa-jDnBt0T&index=4"
            />
            <TutorialItem
              thumbnail={shapesThumbnail}
              url="https://www.youtube.com/watch?v=aztJrrwieco&list=PLD92677OPLExkYYXecx1r4Npa-jDnBt0T&index=5"
            />
          </HStack>
        )}
      </PanelContent>
    </Panel>
  );
};
